import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  LogOut, 
  Upload, 
  FileText, 
  Download, 
  Trash2,
  User,
  Briefcase,
  Calendar,
  MapPin,
  Phone,
  Mail,
  DollarSign,
  Eye,
  Cake,
  Edit,
  Camera,
  Clock,
  UserMinus,
  UserPlus,
  History
} from 'lucide-react';
import { formatearFecha, formatearFechaHora } from '@/utils/dateUtils';
import TerminacionLaboralModal from '@/components/TerminacionLaboralModal';
import ReingresoModal from '@/components/ReingresoModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIPOS_DOCUMENTOS = [
  'Documento de Identificación',
  'Permiso Migratorio',
  'Epicrisis del INS',
  'Carta de Delincuencia',
  'CV',
  'Foto cuenta IBAN',
  'Inclusión Adelantada CCSS',
  'Inclusión Provisional INS',
  'Otro'
];

export default function Expediente({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [trabajador, setTrabajador] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState(TIPOS_DOCUMENTOS[0]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, documentoId: null });
  const [viewDialog, setViewDialog] = useState({ open: false, document: null });
  const [terminacionModal, setTerminacionModal] = useState(false);
  const [reingresoModal, setReingresoModal] = useState(false);

  useEffect(() => {
    loadTrabajador();
  }, [id]);

  const loadTrabajador = async () => {
    try {
      const response = await axios.get(`${API}/trabajadores/${id}`);
      setTrabajador(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Trabajador no encontrado. Puede haber sido eliminado.');
      } else {
        toast.error('Error al cargar expediente del trabajador');
      }
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('tipo', tipoDocumento);
    formData.append('uploaded_by', user.username);

    try {
      await axios.post(`${API}/trabajadores/${id}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento subido exitosamente');
      setSelectedFile(null);
      loadTrabajador();
    } catch (error) {
      toast.error('Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocumento = async () => {
    try {
      await axios.delete(`${API}/trabajadores/${id}/documentos/${deleteDialog.documentoId}?deleted_by=${user.username}`);
      toast.success('Documento eliminado exitosamente');
      loadTrabajador();
    } catch (error) {
      toast.error('Error al eliminar documento');
    } finally {
      setDeleteDialog({ open: false, documentoId: null });
    }
  };

  const handleTerminacionLaboral = async (formData) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('tipo_terminacion', formData.tipo_terminacion);
      formDataToSend.append('fecha_terminacion', formData.fecha_terminacion);
      formDataToSend.append('motivo', formData.motivo || '');
      formDataToSend.append('observaciones', formData.observaciones || '');
      if (formData.monto_liquidacion) {
        formDataToSend.append('monto_liquidacion', formData.monto_liquidacion);
      }
      formDataToSend.append('registrado_por', user.username);

      await axios.post(`${API}/trabajadores/${id}/terminacion-laboral`, formDataToSend);
      
      const tipoTexto = {
        'despido': 'Despido',
        'renuncia': 'Renuncia',
        'liquidacion': 'Liquidación'
      }[formData.tipo_terminacion];
      
      toast.success(`${tipoTexto} registrado exitosamente`);
      setTerminacionModal(false);
      loadTrabajador();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar terminación laboral');
    }
  };

  const handleReingreso = async (nuevaFechaIngreso) => {
    try {
      const formData = new FormData();
      formData.append('nueva_fecha_ingreso', nuevaFechaIngreso);
      formData.append('registrado_por', user.username);

      await axios.post(`${API}/trabajadores/${id}/reingreso`, formData);
      toast.success('Re-ingreso registrado exitosamente');
      setReingresoModal(false);
      loadTrabajador();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar re-ingreso');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Activo': 'bg-green-100 text-green-800 border-green-200',
      'Inactivo': 'bg-gray-100 text-gray-800 border-gray-200',
      'Despedido': 'bg-red-100 text-red-800 border-red-200',
      'Liquidado': 'bg-blue-100 text-blue-800 border-blue-200',
      'Renunció': 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return <Badge className={`${variants[status] || 'bg-gray-100 text-gray-800'} border`}>{status}</Badge>;
  };

  const getTimeUntilBirthday = (fechaNacimiento) => {
    const today = new Date();
    const birthDate = new Date(fechaNacimiento);
    
    // Get this year's birthday
    let nextBirthday = new Date(
      today.getFullYear(),
      birthDate.getMonth(),
      birthDate.getDate()
    );
    
    // If birthday already passed this year, calculate for next year
    if (nextBirthday < today) {
      nextBirthday.setFullYear(today.getFullYear() + 1);
    }
    
    // Calculate difference in years, months, and days
    let años = nextBirthday.getFullYear() - today.getFullYear();
    let meses = nextBirthday.getMonth() - today.getMonth();
    let días = nextBirthday.getDate() - today.getDate();
    
    // Adjust for negative days
    if (días < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(nextBirthday.getFullYear(), nextBirthday.getMonth(), 0).getDate();
      días += ultimoDiaMesAnterior;
    }
    
    // Adjust for negative months
    if (meses < 0) {
      años--;
      meses += 12;
    }
    
    // Build the string
    const partes = [];
    if (años > 0) partes.push(`${años} ${años === 1 ? 'año' : 'años'}`);
    if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mes' : 'meses'}`);
    if (días > 0) partes.push(`${días} ${días === 1 ? 'día' : 'días'}`);
    
    return partes.join(', ') || 'Hoy es su cumpleaños';
  };

  const getEdad = (fechaNacimiento) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    
    return edad;
  };

  const getTiempoActivo = (fechaIngreso) => {
    const inicio = new Date(fechaIngreso);
    const hoy = new Date();
    
    let años = hoy.getFullYear() - inicio.getFullYear();
    let meses = hoy.getMonth() - inicio.getMonth();
    let días = hoy.getDate() - inicio.getDate();
    
    // Adjust for negative days
    if (días < 0) {
      meses--;
      const ultimoDiaMesAnterior = new Date(hoy.getFullYear(), hoy.getMonth(), 0).getDate();
      días += ultimoDiaMesAnterior;
    }
    
    // Adjust for negative months
    if (meses < 0) {
      años--;
      meses += 12;
    }
    
    const partes = [];
    if (años > 0) partes.push(`${años} ${años === 1 ? 'año' : 'años'}`);
    if (meses > 0) partes.push(`${meses} ${meses === 1 ? 'mes' : 'meses'}`);
    if (días > 0) partes.push(`${días} ${días === 1 ? 'día' : 'días'}`);
    
    return partes.join(', ') || '0 días';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="text-emerald-800 text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-emerald-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/rrhh')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-xl font-bold text-emerald-900">Expediente del Trabajador</h1>
              <p className="text-xs text-emerald-600">
                {trabajador.nombre} {trabajador.primer_apellido} {trabajador.segundo_apellido}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Worker Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0 }}
            >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl text-emerald-900 flex items-center">
                    <User className="w-5 h-5 mr-2" />
                    I. Datos Personales
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(trabajador.status)}
                      {trabajador.status === 'Activo' && (
                        <p className="text-sm text-emerald-700 flex items-center font-medium">
                          <Briefcase className="w-3.5 h-3.5 mr-1" />
                          {getTiempoActivo(trabajador.fecha_ingreso)} en la empresa
                        </p>
                      )}
                    </div>
                    
                    {/* Botón de Terminación Laboral (solo si está Activo) */}
                    {trabajador.status === 'Activo' && (
                      <Button
                        onClick={() => setTerminacionModal(true)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        Terminación
                      </Button>
                    )}
                    
                    {/* Botón de Re-ingreso (solo si está Inactivo) */}
                    {trabajador.status === 'Inactivo' && (
                      <Button
                        onClick={() => setReingresoModal(true)}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        RE INGRESO
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Profile Picture Section */}
                <div className="flex justify-start mb-6">
                  {trabajador.profile_picture ? (
                    <img 
                      src={`${BACKEND_URL}${trabajador.profile_picture}`} 
                      alt={`${trabajador.nombre} ${trabajador.primer_apellido}`}
                      className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-emerald-200"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg border-4 border-emerald-200">
                      <span className="text-5xl font-bold text-white">
                        {trabajador.nombre[0]}{trabajador.primer_apellido[0]}
                      </span>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nombre Completo</p>
                    <p className="font-semibold text-gray-900" data-testid="trabajador-nombre">
                      {trabajador.nombre} {trabajador.primer_apellido} {trabajador.segundo_apellido}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Identificación</p>
                    <p className="font-semibold text-gray-900">
                      {trabajador.tipo_identificacion} - {trabajador.numero_identificacion}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fecha de Nacimiento</p>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">{trabajador.fecha_nacimiento}</p>
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 border">
                        {getEdad(trabajador.fecha_nacimiento)} años
                      </Badge>
                      {trabajador.status === 'Activo' && (
                        <div className="relative group">
                          <Cake className="w-5 h-5 text-pink-500 cursor-pointer hover:text-pink-600 transition-colors" />
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-10">
                            <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap shadow-lg">
                              Cumpleaños en {getTimeUntilBirthday(trabajador.fecha_nacimiento)}
                              <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Nacionalidad</p>
                    <p className="font-semibold text-gray-900">{trabajador.nacionalidad}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Sexo</p>
                    <p className="font-semibold text-gray-900">{trabajador.sexo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Estado Civil</p>
                    <p className="font-semibold text-gray-900">{trabajador.estado_civil}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-xl text-emerald-900 flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Teléfono</p>
                      <p className="font-semibold text-gray-900">{trabajador.telefono}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Correo Electrónico</p>
                      <p className="font-semibold text-gray-900">{trabajador.correo_electronico}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="text-sm text-gray-500">Residencia</p>
                      <p className="font-semibold text-gray-900">{trabajador.lugar_residencia}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-semibold text-emerald-900 mb-2">Contacto de Emergencia</p>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm text-gray-500">Nombre</p>
                      <p className="font-semibold text-gray-900">{trabajador.nombre_contacto_emergencia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Parentesco</p>
                      <p className="font-semibold text-gray-900">{trabajador.parentesco_contacto_emergencia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Teléfono</p>
                      <p className="font-semibold text-gray-900">{trabajador.telefono_contacto_emergencia}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            </motion.div>

            {/* Labor Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-xl text-emerald-900 flex items-center">
                  <Briefcase className="w-5 h-5 mr-2" />
                  II. Naturaleza del Contrato
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Fecha de Ingreso</p>
                    <p className="font-semibold text-gray-900">{trabajador.fecha_ingreso}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Ocupación</p>
                    <p className="font-semibold text-gray-900">{trabajador.ocupacion}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Departamento</p>
                    <p className="font-semibold text-gray-900">{trabajador.departamento}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Labor Específica</p>
                    <p className="font-semibold text-gray-900">{trabajador.labor_especifica}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Lugar de Trabajo</p>
                    <p className="font-semibold text-gray-900">{trabajador.lugar_trabajo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tipo de Contrato</p>
                    <p className="font-semibold text-gray-900">{trabajador.tipo_contrato}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </motion.div>

            {/* Condiciones Laborales */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-xl text-emerald-900 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  III. Condiciones Laborales
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Jornada Laboral</p>
                    <p className="font-semibold text-gray-900">{trabajador.jornada_laboral}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Manera de Jornada</p>
                    <p className="font-semibold text-gray-900">{trabajador.manera_jornada}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Garantías Sociales</p>
                    <p className="font-semibold text-gray-900">{trabajador.garantias_sociales?.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Horario</p>
                    <p className="font-semibold text-gray-900">{trabajador.horario_trabajo}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hora de Entrada</p>
                    <p className="font-semibold text-gray-900">{trabajador.hora_entrada}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Hora de Salida</p>
                    <p className="font-semibold text-gray-900">{trabajador.hora_salida}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Días Laborales</p>
                    <p className="font-semibold text-gray-900">{trabajador.dias_laborales?.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Día Libre</p>
                    <p className="font-semibold text-gray-900">{trabajador.dia_libre}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </motion.div>

            {/* Salary Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-xl text-emerald-900 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  IV. Salario y Forma de Pago
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Salario Mínimo</p>
                    <p className="font-semibold text-gray-900">₡{trabajador.salario_minimo.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fijación Salarial</p>
                    <p className="font-semibold text-gray-900">{trabajador.fijacion_salarial}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Modalidad de Pago</p>
                    <div className="space-y-1">
                      {trabajador.monto_por_tm && (
                        <p className="font-semibold text-gray-900">Por TM: ₡{trabajador.monto_por_tm.toLocaleString()}</p>
                      )}
                      {trabajador.salario_diario && (
                        <p className="font-semibold text-gray-900">Salario Diario: ₡{trabajador.salario_diario.toLocaleString()}</p>
                      )}
                      {trabajador.monto_por_saco && (
                        <p className="font-semibold text-gray-900">Por Saco: ₡{trabajador.monto_por_saco.toLocaleString()}</p>
                      )}
                      {trabajador.monto_por_mata && (
                        <p className="font-semibold text-gray-900">Por Mata: ₡{trabajador.monto_por_mata.toLocaleString()}</p>
                      )}
                      {!trabajador.monto_por_tm && !trabajador.salario_diario && !trabajador.monto_por_saco && !trabajador.monto_por_mata && (
                        <p className="font-semibold text-gray-900">No especificado</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Forma de Pago</p>
                    <p className="font-semibold text-gray-900">{trabajador.forma_pago}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Periodicidad de Pago</p>
                    <p className="font-semibold text-gray-900">{trabajador.periodicidad_pago}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Garantías Sociales</p>
                    <div className="space-y-2">
                      <p className="font-semibold text-gray-900">
                        {trabajador.garantias_sociales.length > 0 ? trabajador.garantias_sociales.join(', ') : 'Ninguna'}
                      </p>
                      {!trabajador.garantias_sociales.includes('CCSS') && trabajador.razon_sin_ccss && (
                        <p className="text-sm text-yellow-800 bg-yellow-50 p-2 rounded border border-yellow-200">
                          <strong>Sin CCSS:</strong> {trabajador.razon_sin_ccss}
                        </p>
                      )}
                      {!trabajador.garantias_sociales.includes('INS') && trabajador.razon_sin_ins && (
                        <p className="text-sm text-orange-800 bg-orange-50 p-2 rounded border border-orange-200">
                          <strong>Sin INS:</strong> {trabajador.razon_sin_ins}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Talla de Botas</p>
                    <p className="font-semibold text-gray-900">{trabajador.talla_botas}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            </motion.div>
          </div>

          {/* Right Column - Documents */}
          <div className="space-y-6">
            {/* Upload Document */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-lg text-emerald-900 flex items-center">
                  <Upload className="w-5 h-5 mr-2" />
                  Subir Documento
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label>Tipo de Documento</Label>
                  <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
                    <SelectTrigger data-testid="tipo-documento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_DOCUMENTOS.map(tipo => (
                        <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Archivo</Label>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    data-testid="file-input"
                  />
                </div>
                <Button
                  onClick={handleFileUpload}
                  disabled={uploading || !selectedFile}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="upload-button"
                >
                  {uploading ? 'Subiendo...' : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Subir Documento
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Documents List */}
            <Card className="border-0 shadow-md">
              <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                <CardTitle className="text-lg text-emerald-900 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Documentos ({trabajador.documentos?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {trabajador.documentos && trabajador.documentos.length > 0 ? (
                  <div className="space-y-3">
                    {trabajador.documentos.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="p-3 border rounded-lg hover:bg-emerald-50 transition-colors"
                        data-testid={`documento-${doc.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0 pr-2">
                            <p className="font-semibold text-gray-900 text-sm">{doc.tipo}</p>
                            <p className="text-xs text-gray-500 mt-1 truncate" title={doc.filename}>{doc.filename}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatearFecha(doc.uploaded_at)}
                            </p>
                          </div>
                          <div className="flex space-x-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setViewDialog({ open: true, document: doc })}
                              data-testid={`view-${doc.id}`}
                              className="border-blue-200 hover:bg-blue-50 text-blue-600"
                              title="Ver documento"
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = `${BACKEND_URL}${doc.path}`;
                                link.download = doc.filename;
                                link.click();
                              }}
                              data-testid={`download-${doc.id}`}
                              className="border-green-200 hover:bg-green-50 text-green-600"
                              title="Descargar documento"
                            >
                              <Download className="w-3 h-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteDialog({ open: true, documentoId: doc.id })}
                              data-testid={`delete-${doc.id}`}
                              className="border-red-200 hover:bg-red-50 text-red-600"
                              title="Eliminar documento"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500" data-testid="no-documentos">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No hay documentos subidos</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Historial Laboral */}
            {trabajador.historial_laboral && trabajador.historial_laboral.length > 0 && (
              <Card className="border-0 shadow-md">
                <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-lg text-blue-900 flex items-center">
                    <History className="w-5 h-5 mr-2" />
                    Historial Laboral
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Registro de contrataciones, terminaciones y reingresos del trabajador
                  </p>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {trabajador.historial_laboral
                      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                      .map((evento, index) => {
                        const isActive = index === 0 && trabajador.status === 'Activo';
                        const tipoTexto = {
                          'reingreso': 'Re-ingreso',
                          'despido': 'Despido',
                          'renuncia': 'Renuncia',
                          'liquidacion': 'Liquidación'
                        }[evento.tipo] || evento.tipo;
                        
                        const tipoColor = {
                          'reingreso': 'border-emerald-500 bg-emerald-50',
                          'despido': 'border-red-500 bg-red-50',
                          'renuncia': 'border-yellow-500 bg-yellow-50',
                          'liquidacion': 'border-blue-500 bg-blue-50'
                        }[evento.tipo] || 'border-gray-500 bg-gray-50';
                        
                        const tipoIcon = {
                          'reingreso': <UserPlus className="w-5 h-5 text-emerald-600" />,
                          'despido': <UserMinus className="w-5 h-5 text-red-600" />,
                          'renuncia': <UserMinus className="w-5 h-5 text-yellow-600" />,
                          'liquidacion': <DollarSign className="w-5 h-5 text-blue-600" />
                        }[evento.tipo] || <Calendar className="w-5 h-5 text-gray-600" />;
                        
                        return (
                          <div key={index} className={`border-l-4 ${tipoColor} p-4 rounded-r-lg`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="mt-1">
                                  {tipoIcon}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">{tipoTexto}</h4>
                                    {isActive && (
                                      <Badge className="bg-green-100 text-green-800 border-green-200 border text-xs">
                                        Periodo Actual
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                    <div>
                                      <span className="text-gray-500">Fecha Ingreso:</span>
                                      <span className="ml-2 font-medium text-gray-900">{evento.fecha_ingreso}</span>
                                    </div>
                                    {evento.fecha_terminacion && (
                                      <div>
                                        <span className="text-gray-500">Fecha Terminación:</span>
                                        <span className="ml-2 font-medium text-gray-900">{evento.fecha_terminacion}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {evento.motivo && (
                                    <p className="text-sm text-gray-600 mt-2">
                                      <span className="font-medium">Motivo:</span> {evento.motivo}
                                    </p>
                                  )}
                                  
                                  {evento.observaciones && (
                                    <p className="text-sm text-gray-600 mt-1">
                                      <span className="font-medium">Observaciones:</span> {evento.observaciones}
                                    </p>
                                  )}
                                  
                                  {evento.monto_liquidacion && (
                                    <p className="text-sm text-gray-700 mt-2 font-medium">
                                      Monto de Liquidación: ₡{parseFloat(evento.monto_liquidacion).toLocaleString('es-CR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </p>
                                  )}
                                  
                                  <p className="text-xs text-gray-500 mt-2">
                                    Registrado por: {evento.registrado_por} • {formatearFechaHora(evento.created_at)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Timeline del Expediente - Historial Completo */}
            {(trabajador.historial_cambios || trabajador.documentos) && (
              <Card className="border-0 shadow-md">
                <CardHeader className="border-b bg-gradient-to-r from-emerald-50 to-green-50">
                  <CardTitle className="text-lg text-emerald-900 flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Historial Completo del Expediente
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">
                    Registro de todos los cambios y movimientos realizados en el expediente
                  </p>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="relative">
                    {/* Vertical line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-emerald-200"></div>
                    
                    {/* Timeline items */}
                    <div className="space-y-6">
                      {(() => {
                        const historialItems = [];
                        
                        // Agregar items del historial de cambios
                        if (trabajador.historial_cambios) {
                          trabajador.historial_cambios.forEach(cambio => {
                            historialItems.push({
                              ...cambio,
                              fecha: cambio.fecha,
                              esHistorial: true
                            });
                          });
                        }
                        
                        // Ordenar por fecha descendente
                        return historialItems
                          .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                          .map((item, index) => {
                            // Determinar icono y color según el tipo
                            let icon = <FileText className="w-4 h-4 text-white" />;
                            let bgColor = 'bg-emerald-600';
                            
                            if (item.tipo === 'creacion') {
                              icon = <span className="text-white font-bold text-lg">+</span>;
                              bgColor = 'bg-blue-600';
                            } else if (item.tipo === 'modificacion_campo') {
                              icon = <Edit className="w-4 h-4 text-white" />;
                              bgColor = 'bg-amber-600';
                            } else if (item.tipo === 'documento_subido') {
                              icon = <Upload className="w-4 h-4 text-white" />;
                              bgColor = 'bg-green-600';
                            } else if (item.tipo === 'documento_eliminado') {
                              icon = <Trash2 className="w-4 h-4 text-white" />;
                              bgColor = 'bg-red-600';
                            } else if (item.tipo === 'foto_actualizada') {
                              icon = <Camera className="w-4 h-4 text-white" />;
                              bgColor = 'bg-purple-600';
                            } else if (item.tipo === 'terminacion_laboral') {
                              icon = <UserMinus className="w-4 h-4 text-white" />;
                              bgColor = 'bg-orange-600';
                            } else if (item.tipo === 'reingreso') {
                              icon = <UserPlus className="w-4 h-4 text-white" />;
                              bgColor = 'bg-teal-600';
                            }
                            
                            return (
                              <div key={item.id || index} className="relative pl-12">
                                {/* Timeline dot */}
                                <div className={`absolute left-0 top-1 w-8 h-8 ${bgColor} rounded-full flex items-center justify-center shadow-md`}>
                                  {icon}
                                </div>
                                
                                {/* Content */}
                                <div className="bg-white p-4 rounded-lg border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-900">{item.descripcion}</p>
                                    {item.usuario && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Por: {item.usuario}
                                      </p>
                                    )}
                                    {item.valor_anterior && item.valor_nuevo && (
                                      <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                                        <span className="text-red-600 line-through">{item.valor_anterior}</span>
                                        {' → '}
                                        <span className="text-green-600 font-semibold">{item.valor_nuevo}</span>
                                      </div>
                                    )}
                                    <p className="text-xs text-gray-400 mt-2 flex items-center">
                                      <Clock className="w-3 h-3 mr-1" />
                                      {formatearFechaHora(item.fecha)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                      })()}
                      
                      {(!trabajador.historial_cambios || trabajador.historial_cambios.length === 0) && (
                        <p className="text-center text-gray-500 py-8">
                          No hay cambios registrados en el historial
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Note about email automation */}
            <Card className="border-0 shadow-md bg-blue-50">
              <CardContent className="pt-6">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> El envío automático de correos (bienvenida, cumpleaños, documentos) está actualmente <strong>en modo simulado</strong>. Se puede implementar más adelante con servicios como SendGrid o AWS SES.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* View Document Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ open, document: null })}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>
              {viewDialog.document?.tipo} - {viewDialog.document?.filename}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden">
            {viewDialog.document && (
              <iframe
                src={`${BACKEND_URL}${viewDialog.document.path}`}
                className="w-full h-full border-0"
                title="Vista previa del documento"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, documentoId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El documento se eliminará permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-doc">Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteDocumento} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-doc"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de Terminación Laboral */}
      <TerminacionLaboralModal
        open={terminacionModal}
        onClose={() => setTerminacionModal(false)}
        onSubmit={handleTerminacionLaboral}
        trabajadorNombre={`${trabajador?.nombre} ${trabajador?.primer_apellido} ${trabajador?.segundo_apellido || ''}`}
      />

      {/* Modal de Re-ingreso */}
      <ReingresoModal
        open={reingresoModal}
        onClose={() => setReingresoModal(false)}
        onSubmit={handleReingreso}
        trabajadorNombre={`${trabajador?.nombre} ${trabajador?.primer_apellido} ${trabajador?.segundo_apellido || ''}`}
      />
    </div>
  );
}
